<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { makeWebsiteRequest, splitDateStringToDateTimeParts } from '../../Common/tools';
  import Table from '../../Table/Table.svelte';
  import { External, M } from '../../external';
  import { showSubscriptionDetails } from './SubscriptionDetails.svelte';

  export let period = null,
    funnel = null,
    filters = [],
    showArchived = false,
    contact = 0,
    search = '',
    options = {};

  const dispatch = createEventDispatcher();

  let loading = true,
    initialLoad = true,
    currentPage = 0,
    pagesCount = 0,
    itemsCount = 0,
    table = null,
    selection = null;

  let tableColumns = [
    ...(contact ? [] : [{
      key: 'customerName',
      label: M.NAME,
      class: 'flex-none mw-180',
      row: 'modified',
      style: 'font-size: 13px',
      rowLabel: (subscription, column) => {
        return subscription.customerEmail;
      }
    }]),
    { key: 'productName', label: M.PRODUCT, class: 'flex-none mw-180' },
    {
      key: 'priceLbl',
      label: M.PRICE,
      class: 'flex-none'
    },
    {
      key: 'cycleLbl',
      label: M.CYCLE,
      class: 'flex-none mw-140',
      row: 'modified',
      style: 'font-size: 13px',
      rowLabel: (subscription, column) => {
        return subscription.paymentsLbl;
      }
    },
    /*{
      key: "createdDate",
      label: M.CREATED_ON,
      class: "flex-none mw-140",
      row: "modified",
      style: "font-size: 13px",
      rowLabel: (subscription, column) => {
        return subscription.createdTime;
      }
    },*/
    {
      key: 'lastChargeDate',
      label: M.LAST_PAYMENT,
      class: 'flex-none mw-140',
      row: 'modified',
      style: 'font-size: 13px',
      rowLabel: (subscription, column) => {
        return subscription.lastChargeTime;
      }
    },
    {
      key: showArchived ? 'expirationDate' : 'nextChargeDate',
      label: showArchived ? M.EXPIRED_ON : M.NEXT_PAYMENT,
      class: 'flex-none mw-140',
      row: 'modified',
      style: 'font-size: 13px',
      rowLabel: (subscription, column) => {
        return showArchived ? subscription.expirationTime : subscription.nextChargeTime;
      }
    },
    ...(showArchived
      ? []
      : [
        {
          label: '',
          type: 'icon',
          class: 'mw-40',
          iconClass: 'material-icons-outlined',
          iconText: 'build_circle',
          onClick: (subscription, column) => {
            showDetails(subscription);
          }
        }
      ])
  ];

  let tableData = {
    columns: tableColumns,
    rows: []
  };

  let setRequestFilter = (request, stringify) => {
    request = request || {};

    request.contact = contact;

    if (filters.length > 0) {
      request.filters = stringify ? JSON.stringify(filters) : filters;
    }

    if (funnel) {
      request.funnel = funnel;
    }
    if (period && period.from) {
      request.from = External.Tools.dateToISOStr(period.from);
    }

    if (period && period.to) {
      request.to = External.Tools.dateToISOStr(period.to);
    }

    request.search = search;
    request.archived = showArchived;
    return request;
  };

  let load = async (page) => {
    if (loading && !initialLoad) {
      return;
    }
    initialLoad = false;

    if (page === 0) {
      tableData.rows = [];
      selection = null;
    } else if (currentPage + 1 >= pagesCount) {
      return;
    }
    loading = true;

    let request = setRequestFilter({
      page: page
    }, true);

    let response = await makeWebsiteRequest({
      action: 'getStoreSubscriptions',
      method: 'post',
      data: request
    });

    loading = false;
    if (!response.data) return;

    currentPage = page;
    pagesCount = response.data.pagesCount;
    itemsCount = response.data.count;

    response.data.items.forEach((item) => {
      item.paidLbl = item.paid ? M.PAID : M.NOT_PAID;
      let lastBillingDate = splitDateStringToDateTimeParts(item.lastBillingDate || '');
      let nextBillingDate = splitDateStringToDateTimeParts(item.nextBillingDate || '');
      let expirationDate = splitDateStringToDateTimeParts(item.expirationDate || '');
      let createdDate = splitDateStringToDateTimeParts(item.createdDate || '');
      item.lastChargeDate = lastBillingDate.date;
      item.lastChargeTime = lastBillingDate.time;
      item.nextChargeDate = nextBillingDate.date;
      item.nextChargeTime = nextBillingDate.time;
      item.expirationDate = expirationDate.date;
      item.expirationTime = expirationDate.time;
      item.createdDate = createdDate.date;
      item.createdTime = createdDate.time;
      item.priceLbl = External.WebPlatform.currToStr(item.price, External.Website.storeCurrencySymbol, External.Website.storeCurrencyPositionAtLeft, External.Website.storeCurrency, External.Website.storeCountry);

      if (item.cycles) {
        item.paymentsLbl = External.format(M.PAYMENT_NO, 'curr', item.charges, 'all', item.cycles); // "Payment " + item.charges + " of " + item.cycles;
      } else {
        item.paymentsLbl = '';
      }

      if (item.period === 1) {
        item.cycleLbl = item.periodUnit === 'MONTHLY' ? M.MONTHLY : M.WEEKLY;
      } else if (item.period === 12 && item.periodUnit === 'MONTHLY') {
        item.cycleLbl = M.YEARLY;
      } else {
        item.cycleLbl = item.period + '-' + (item.periodUnit === 'MONTHLY' ? M.MONTHS : M.WEEKS);
      }
    });

    if (page === 0) {
      dispatch('itemsloaded', {
        count: itemsCount
      });
    }
    if (table) table.addRows(response.data.items);
  };

  let onNext = () => {
    load(currentPage + 1);
  };

  let showDetails = (subscription) => {
    showSubscriptionDetails(subscription, {
      onActivated: (subscription) => {
        table.removeRowById(subscription.id);
      },
      onCanceled: (subscription) => {
        table.removeRowById(subscription.id);
      },
      onUpdated: (subscription) => {
        table.updateRow(subscription);
      }
    });
  };

  export function reload() {
    load(0);
  }

  const getSelectedItemsCount = () => {
    if (selection !== null) {
      if (selection.all) return itemsCount - selection.unselected.length;
      else return selection.selected.length;
    }
    return itemsCount;
  };

  onMount(() => {
    load(0);
  });
</script>

<Table
  bind:this={table}
  activeColumns={tableData.columns}
  data={tableData.rows}
  loading={loading}
  hideHeadOnLoad={true}
  hasSelection={false}
  bind:selection
  onClick={showDetails}
  nextPage={onNext}
  tableClass={"orders-scrollable-table"}>
  <div class="col20 f-left coming-soon-block">
    <div class="col20 f-left t-center mb-25">
      <span class="material-icons-outlined fingerprint-icon" style="background: #f5f6fa;"> bubble_chart </span>
    </div>
    <div class="col20 f-left t-center">
      <h3>{M.NO_DATA_YET}</h3>
    </div>
  </div>
</Table>
